import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CodeInput from './components/CodeInput';
import ApiSettings from './components/ApiSettings';
import ConversionResult from './components/ConversionResult';
import ScreenshotUploader from './components/ScreenshotUploader';
import Header from './components/Header';
import './App.css';

function App() {
  const [htmlCode, setHtmlCode] = useState('');
  const [cssCode, setCssCode] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [modelName, setModelName] = useState('anthropic/claude-3.5-sonnet');
  const [apiUrl, setApiUrl] = useState('https://openrouter.ai/api/v1/chat/completions');
  const [apiProvider, setApiProvider] = useState('openrouter');
  const [isConverting, setIsConverting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [screenshots, setScreenshots] = useState([]);

  // Load saved settings on component mount
  useEffect(() => {
    const savedApiKey = localStorage.getItem('blocksmith_api_key');
    const savedModelName = localStorage.getItem('blocksmith_model_name');
    const savedApiUrl = localStorage.getItem('blocksmith_api_url');
    const savedApiProvider = localStorage.getItem('blocksmith_api_provider');

    // Load provider-specific API keys
    const savedOpenrouterKey = localStorage.getItem('blocksmith_openrouter_key');
    const savedOpenaiKey = localStorage.getItem('blocksmith_openai_key');
    const savedStraicoKey = localStorage.getItem('blocksmith_straico_key');
    const savedCustomKey = localStorage.getItem('blocksmith_custom_key');

    if (savedApiProvider) setApiProvider(savedApiProvider);
    if (savedModelName) setModelName(savedModelName);
    if (savedApiUrl) setApiUrl(savedApiUrl);

    // Set the correct API key based on the provider
    if (savedApiProvider === 'openrouter' && savedOpenrouterKey) {
      setApiKey(savedOpenrouterKey);
    } else if (savedApiProvider === 'openai' && savedOpenaiKey) {
      setApiKey(savedOpenaiKey);
    } else if (savedApiProvider === 'straico' && savedStraicoKey) {
      setApiKey(savedStraicoKey);
    } else if (savedApiProvider === 'custom' && savedCustomKey) {
      setApiKey(savedCustomKey);
    } else if (savedApiKey) {
      // Fallback to the generic key if specific one not found
      setApiKey(savedApiKey);
    }
  }, []);

  // Save settings when they change
  useEffect(() => {
    // Save the provider-specific API key
    if (apiProvider === 'openrouter') {
      localStorage.setItem('blocksmith_openrouter_key', apiKey);
    } else if (apiProvider === 'openai') {
      localStorage.setItem('blocksmith_openai_key', apiKey);
    } else if (apiProvider === 'straico') {
      localStorage.setItem('blocksmith_straico_key', apiKey);
    } else if (apiProvider === 'custom') {
      localStorage.setItem('blocksmith_custom_key', apiKey);
    }
    
    // Always save general settings
    localStorage.setItem('blocksmith_api_key', apiKey);
    localStorage.setItem('blocksmith_model_name', modelName);
    localStorage.setItem('blocksmith_api_url', apiUrl);
    localStorage.setItem('blocksmith_api_provider', apiProvider);
  }, [apiKey, modelName, apiUrl, apiProvider]);

  const extractStylesFromHTML = (html) => {
    // Only extract if we don't already have CSS content
    if (cssCode.trim()) {
      return html;
    }

    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    let match;
    let extractedStyles = '';
    let cleanedHtml = html;

    // Extract all style tags content
    while ((match = styleRegex.exec(html)) !== null) {
      extractedStyles += match[1] + '\n';
      // Remove style tags from HTML
      cleanedHtml = cleanedHtml.replace(match[0], '');
    }

    if (extractedStyles) {
      setCssCode(extractedStyles);
    }

    return cleanedHtml;
  };

  const handleHtmlChange = (html) => {
    const cleanedHtml = extractStylesFromHTML(html);
    setHtmlCode(cleanedHtml);
  };

  // Function to prepare image data for API requests
  const prepareImageData = (dataUrl) => {
    // For Straico, we need to return the dataUrl directly
    // as they expect the full data URL with base64 content
    return dataUrl;
  };

  const convertToElementor = async () => {
    if (!htmlCode.trim()) {
      setError('Please provide HTML code');
      return;
    }

    // For custom API providers, API key might be optional
    if ((apiProvider !== 'custom' && !apiKey.trim()) || !modelName.trim()) {
      setError('Please provide required API information');
      return;
    }

    setIsConverting(true);
    setError(null);
    setResult(null);

    try {
      let screenshotPrompt = '';

      // Add screenshot references if available
      if (screenshots.length > 0) {
        screenshotPrompt = `
          IMPORTANT: I'm providing ${screenshots.length} screenshot(s) as visual references for this conversion:
          ${screenshots.map((screenshot, index) => `[Screenshot ${index + 1}]: ${screenshot.name}`).join('\n')}

          Please analyze these screenshots to understand the visual layout, styling, and design.
          The goal is to create Elementor JSON that closely matches the visual appearance shown in these images.
          Use the screenshots as a guide for:
          - Layout structure and spacing
          - Visual hierarchy
          - Color scheme and typography
          - Component styling and positioning
          - Responsive behavior and breakpoints
        `;
      }

      const prompt = `You are an expert WordPress Elementor developer. Convert the following HTML and CSS code into ELEMENTOR JSON format that can be imported directly into WordPress Elementor.

${screenshotPrompt}

CRITICAL REQUIREMENTS:
1. ONLY OUTPUT VALID ELEMENTOR JSON FORMAT. DO NOT OUTPUT ANY OTHER PAGE BUILDER FORMAT.
2. Generate NATIVE Elementor JSON - NOT an HTML embed widget or shortcode.
3. Use only native Elementor widgets (heading, text-editor, image, button, spacer, divider, etc.)
4. Convert HTML structure to equivalent Elementor sections, columns, and widgets.
5. Map CSS styles to Elementor's native styling options.
6. The output MUST be valid, parseable JSON that follows Elementor's specific schema.
7. Preserve the visual layout and styling as closely as possible using Elementor's native capabilities.
8. IMPORTANT: Implement full responsive settings for all elements (desktop, tablet, mobile).
9. Convert colors, fonts, spacing, and layout properties to Elementor format.
10. DO NOT include any explanations, comments or markdown in your response.
11. Output MUST be valid, parseable JSON without any surrounding text, markdown formatting, or code blocks.
12. CRITICAL: Ensure all elements have responsive settings for mobile and tablet views.
13. ESSENTIAL: Properly implement layout settings for all containers using flex layout, including:
    - "layout": "flex" for flex containers
    - "flex_direction": "row" or "column"
    - "justify_content": "center", "flex-start", "flex-end", "space-between", etc.
    - "align_items": "center", "flex-start", "flex-end", "stretch", etc.
    - Define proper "gap" settings with unit and size
    - Implement width settings with "width", "min_width", "max_width" when needed

**Elementor JSON Conversion Rules:**
---
**General Structure**
- The top-level structure: {"title": "[SET A RELEVANT TITLE]", "type": "[SET TYPE: page, header, footer, error-404, popup, etc.]", "version": "0.4", "page_settings": [SEE BELOW], "content": [SEE BELOW]}

Page Settings
If the page has no settings: "page_settings": []
If the page has settings (backgrounds, padding, margins, etc.):
"page_settings": {
  "background_background": "classic",
  "background_color": "#FFFFFF",
  "margin": {"unit": "px", "top": "0", "right": "0", "bottom": "0", "left": "0", "isLinked": true},
  "padding": {"unit": "px", "top": "0", "right": "10", "bottom": "0", "left": "10", "isLinked": false}
  // Add other settings as needed
}

WordPress-related data (title, excerpt, featured image, post status) is not stored here.
Elementor-related styling data (backgrounds, margins, paddings, layout settings) is stored here.
Popup pages and certain addons may add custom controls in page_settings.

Page Content (content)
content is an array of objects mapping all page elements. The structure is recursive: each element's elements field holds its children.
Use "container" for modern layouts. Use "section" and "column" only if HTML clearly maps to the old layout.

For each HTML element:
Use "elType": "container" for wrappers/sections/divs.
Use "elType": "widget" for content elements with appropriate "widgetType":
<h1>-<h6> → "heading"
<p> or rich text → "text-editor"
<img> → "image"
<a> or button → "button"
Lists, icons, etc., map to the closest Elementor widget type.

Each element must have a unique "id".
Apply CSS styles to the settings object of each element using Elementor's keys (e.g., background_color, padding).
If a CSS style cannot be mapped directly, include a comment in the JSON for manual review.

General Elements
Every element has these values:
{
  "id": "[UNIQUE STRING]",
  "elType": "[container|widget|section|column]",
  "isInner": false,
  "settings": [],
  "elements": []
}

id: Unique identifier for the element.
elType: The element type ("container", "widget", etc.).
isInner: Whether this is an inner element (usually false unless nested columns).
settings: An object with element/widget settings, or an empty array if none.
elements: Array of nested elements.

When elType is "widget", add "widgetType" (e.g., "heading", "image", "button").

Container Elements
Containers are layout elements with nested capabilities.
Container example:
{
  "id": "[UNIQUE STRING]",
  "elType": "container",
  "isInner": false,
  "settings": [],
  "elements": []
}

Containers can nest other containers or widgets inside their elements array.
If the container has settings (height, background, etc.), add them to the settings object:
{
  "id": "[UNIQUE STRING]",
  "elType": "container",
  "isInner": false,
  "settings": {
    "height": "min-height",
    "custom_height": {"unit": "vh", "size": 70, "sizes": []},
    "content_position": "middle",
    "html_tag": "section"
  },
  "elements": []
}

CRITICAL: Layout Settings for Containers
For containers that need to be laid out with flex, include proper flex settings:
{
  "id": "[UNIQUE STRING]",
  "elType": "container",
  "isInner": false,
  "settings": {
    "layout": "flex",
    "flex_direction": "row",  // or "column"
    "justify_content": "center",  // or "flex-start", "flex-end", "space-between", etc.
    "align_items": "center",  // or "flex-start", "flex-end", "stretch", etc.
    "gap": {
      "unit": "px",
      "size": "20",
      "sizes": []
    },
    "width": {
      "unit": "%",
      "size": "100",
      "sizes": []
    },
    "content_width": "full"
  },
  "elements": []
}

For responsive flex settings:
{
  "layout": "flex",
  "flex_direction": "row",
  "flex_direction_tablet": "column",
  "flex_direction_mobile": "column",
  "justify_content": "space-between",
  "justify_content_tablet": "center",
  "align_items": "center",
  "gap": {"unit": "px", "size": "20", "sizes": []},
  "gap_tablet": {"unit": "px", "size": "15", "sizes": []},
  "gap_mobile": {"unit": "px", "size": "10", "sizes": []}
}

Widget Elements
An Elementor widget is an element with its own custom properties.
Widget structure:
{
  "id": "[UNIQUE STRING]",
  "elType": "widget",
  "widgetType": "[WIDGET TYPE]",
  "isInner": false,
  "settings": [],
  "elements": []
}

widgetType: The widget type (e.g., "heading", "image", "button", etc.).
settings: An object containing all widget settings and control values as key-value pairs.
Some widgets support nested elements (e.g., Menu, Accordion, Tabs, Carousel).
Data from widget controls is stored in settings as key-value pairs.

Examples
Empty page with no settings:
{
  "title": "About Page",
  "type": "page",
  "version": "0.4",
  "page_settings": [],
  "content": []
}

Page with two empty containers:
{
  "title": "Sample Page",
  "type": "page",
  "version": "0.4",
  "page_settings": [],
  "content": [
    {"id": "6af611eb", "elType": "container", "isInner": false, "settings": [], "elements": []},
    {"id": "7fb170b9", "elType": "container", "isInner": false, "settings": [], "elements": []}
  ]
}

Container with nested containers:
{
  ...
  "content": [
    {
      ...
      "elType": "container",
      ...
      "elements": [
        {
          ...
          "elType": "container",
          ...
          "elements": [
            {...}
          ]
        }
      ]
    }
  ]
}

Container with widgets:
{
  ...
  "content": [
    {
      ...
      "elements": [
        {... "elType": "widget", "widgetType": "heading", ...},
        {... "elType": "widget", "widgetType": "image", ...},
        {... "elType": "widget", "widgetType": "button", ...}
      ]
    }
  ]
}

Widget with settings example:
{
  ...
  {
    "id": "[UNIQUE STRING]",
    "elType": "widget",
    "widgetType": "[WIDGET TYPE]",
    "isInner": false,
    "settings": {
      // Widget control key-value pairs
      // Example for heading:
      // title: 'Heading text',
      // align: 'center'
    },
    "elements": []
  }
}

RESPONSIVE DESIGN IN ELEMENTOR:
For responsive design, add these settings:
1. For responsive margins/padding:
   "_margin": {"unit": "px", "top": "10", "right": "10", "bottom": "10", "left": "10", "isLinked": true},
   "_margin_tablet": {"unit": "px", "top": "5", "right": "5", "bottom": "5", "left": "5", "isLinked": true},
   "_margin_mobile": {"unit": "px", "top": "2", "right": "2", "bottom": "2", "left": "2", "isLinked": true}

2. For responsive typography:
   "_typography_typography": "custom",
   "_typography_font_size": {"unit": "px", "size": 40},
   "_typography_font_size_tablet": {"unit": "px", "size": 30},
   "_typography_font_size_mobile": {"unit": "px", "size": 24}

3. For responsive visibility:
   "hide_desktop": "",
   "hide_tablet": "",
   "hide_mobile": ""

4. For responsive column widths:
   "_column_size": 50,
   "_column_size_tablet": 100,
   "_column_size_mobile": 100

5. For responsive flex layout:
   "flex_direction": "row",
   "flex_direction_tablet": "column",
   "flex_direction_mobile": "column",
   "justify_content": "space-between",
   "justify_content_tablet": "center",
   "gap": {"unit": "px", "size": "20", "sizes": []},
   "gap_tablet": {"unit": "px", "size": "15", "sizes": []},
   "gap_mobile": {"unit": "px", "size": "10", "sizes": []}

Instructions:
Map all HTML elements and their structure to Elementor's JSON structure with containers and widgets.
Map all applicable CSS properties to Elementor's settings fields.
Use appropriate nesting for all containers and widgets.
Output ONLY the final valid JSON.

ELEMENTOR JSON STRUCTURE:
The JSON structure consists of an object containing information about the page.
The information includes the document type, it's title and the version.
Furthermore, it holds real data such as document settings and the document content.

Basic structure:
{
  "title": "Template Title",
  "type": "page",
  "version": "0.4",
  "page_settings": [],
  "content": []
}

JSON Values:
- title (string): The title displayed to the user in the WordPress dashboard and the Elementor Editor.
- type (string): The document type. Available values are: header, footer, error-404, popup, page, post, etc.
- version (string): The data structure version. The latest version is 0.4.
- page_settings (array/object): Data from the "Page Setting" panel. An empty array if settings are not defined, an object if the page has settings.
- content (array): An array of objects that holds all the page elements.

ELEMENTOR SETTINGS FORMAT:
- Use objects for values with units: {"unit": "px", "size": 45}
- Background settings: "background_background": "classic", "background_color": "#ffffff"
- Typography: "_element_typography_title_font_family", "_element_typography_title_font_size"
- Widget content: "title" for heading, "editor" for text-editor (HTML string)
- Layout: "content_position", "align_items", "justify_content"
- Container layout: "layout": "flex", "flex_direction": "row", "width": {"unit": "%", "size": 100}

EXAMPLE TEMPLATES:
Header JSON Example:
{
  "title": "Site Header",
  "type": "header",
  "version": "0.4",
  "page_settings": {
    "content_wrapper_html_tag": "header",
    "background_background": "classic",
    "background_color": "#000000"
  },
  "content": [
    {"id": "3130e2cf", "elType": "container", "isInner": false, "settings": [], "elements": []}
  ]
}

Footer JSON Example:
{
  "title": "Site Footer",
  "type": "footer",
  "version": "0.4",
  "page_settings": {
    "content_wrapper_html_tag": "footer"
  },
  "content": [
    {"id": "1aebaeaa", "elType": "container", "isInner": false, "settings": [], "elements": []}
  ]
}

404 page JSON Example:
{
  "title": "404 Page",
  "type": "error-404",
  "version": "0.4",
  "page_settings": {
    "content_wrapper_html_tag": "main",
    "background_background": "classic",
    "background_color": "#333333"
  },
  "content": []
}

Popup JSON Example:
{
  "title": "Mobile Navigation Popup",
  "type": "popup",
  "version": "0.4",
  "page_settings": {
    "width": {"unit": "px", "size": 600, "sizes": []},
    "entrance_animation": "fadeIn",
    "exit_animation": "fadeIn",
    "overlay_background_color": "#000000AA",
    "prevent_scroll": "yes"
  },
  "content": [
    {
      "id": "c647ac2",
      "elType": "container",
      "isInner": false,
      "settings": {
        "padding": {"unit": "px", "top": "20", "right": "20", "bottom": "20", "left": "20", "isLinked": true}
      },
      "elements": []
    }
  ]
}

HTML Code:
${htmlCode}

CSS Code:
${cssCode || 'No CSS provided'}

**Output ONLY the finished JSON.**`;

      // Determine headers based on API URL
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add specific headers based on API provider
      if (apiProvider === 'openrouter') {
        headers['Authorization'] = `Bearer ${apiKey}`;
        headers['HTTP-Referer'] = window.location.origin;
        headers['X-Title'] = 'Blocksmith - HTML to Elementor Converter';
      } else if (apiProvider === 'openai') {
        headers['Authorization'] = `Bearer ${apiKey}`;
      } else if (apiProvider === 'straico') {
        headers['Authorization'] = `Bearer ${apiKey}`;
      } else if (apiProvider === 'custom' && apiKey.trim()) {
        // Only add Authorization header if API key is provided for custom APIs
        headers['Authorization'] = `Bearer ${apiKey}`;
      }

      // Create request body based on API provider
      let requestBody;
      
      if (apiProvider === 'straico') {
        // For Straico, use their specific format
        // Convert screenshots to proper format for Straico API
        let imageUrls = [];
        
        if (screenshots.length > 0) {
          // For Straico, we need to use the base64 data directly
          imageUrls = screenshots.map(screenshot => screenshot.url);
        }
        
        requestBody = {
          models: [modelName],
          message: prompt,
        };
        
        // Only add images if there are screenshots
        if (imageUrls.length > 0) {
          requestBody.images = imageUrls;
        }
      } else if (apiProvider === 'openai' && modelName.includes('gpt-4') && screenshots.length > 0) {
        // For OpenAI with GPT-4 Vision
        const messages = [{
          role: 'user',
          content: [
            { type: 'text', text: prompt }
          ]
        }];
        
        // Add images to the message content
        screenshots.forEach(screenshot => {
          messages[0].content.push({
            type: 'image_url',
            image_url: {
              url: prepareImageData(screenshot.url)
            }
          });
        });
        
        requestBody = {
          model: modelName,
          messages,
          temperature: 0.1,
          max_tokens: 8000
        };
      } else if (apiProvider === 'openrouter' && screenshots.length > 0) {
        // For OpenRouter with vision capabilities
        // Check if the model supports vision
        const supportsVision = modelName.includes('gpt-4') || 
                              modelName.includes('claude-3') || 
                              modelName.includes('gemini') ||
                              modelName.includes('llava');
        
        if (supportsVision) {
          // For models that support vision
          const content = [
            { type: 'text', text: prompt }
          ];
          
          // Add images to the content array (limit to 5 images to avoid token issues)
          const imagesToInclude = screenshots.slice(0, 5);
          imagesToInclude.forEach(screenshot => {
            content.push({
              type: 'image_url',
              image_url: prepareImageData(screenshot.url)
            });
          });
          
          requestBody = {
            model: modelName,
            messages: [{ role: 'user', content }],
            temperature: 0.1,
            max_tokens: 8000
          };
        } else {
          // For models that don't support vision, fall back to text-only
          requestBody = {
            model: modelName,
            messages: [
              { role: 'user', content: prompt }
            ],
            temperature: 0.1,
            max_tokens: 8000
          };
        }
      } else {
        // Standard text-only request for other providers or when no screenshots
        requestBody = {
          model: modelName,
          messages: [
            { role: 'user', content: prompt }
          ],
          temperature: 0.1,
          max_tokens: 8000
        };
      }

      console.log('API URL:', apiUrl);
      console.log('Headers:', headers);
      console.log('Request Body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error?.message || `API request failed: ${response.status}`;
        } catch (e) {
          errorMessage = `API request failed: ${response.status} - ${errorText.slice(0, 100)}${errorText.length > 100 ? '...' : ''}`;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('API Response:', data);
      
      let elementorJson;

      // Handle different response formats based on API provider
      if (apiProvider === 'straico') {
        // Extract the response based on the Straico API structure
        if (data && data.data && data.data.completions) {
          // Get the first model's completion
          const modelKey = Object.keys(data.data.completions)[0];
          if (modelKey && data.data.completions[modelKey].completion) {
            const completion = data.data.completions[modelKey].completion;
            // Get the content from the first choice
            if (completion.choices && completion.choices.length > 0) {
              elementorJson = completion.choices[0].message.content;
            } else {
              throw new Error('No completion choices found in Straico response');
            }
          } else {
            throw new Error('No completion data found in Straico response');
          }
        } else {
          throw new Error('Invalid Straico API response structure');
        }
      } else {
        // Standard OpenAI-compatible response
        elementorJson = data.choices[0]?.message?.content;
      }

      if (!elementorJson) {
        throw new Error('No response content received from API');
      }

      // Clean up the response - remove markdown code blocks if present
      elementorJson = elementorJson.replace(/```json\s*/g, '').replace(/```\s*$/g, '').trim();

      // Try to parse as JSON to validate
      try {
        JSON.parse(elementorJson);
        setResult(elementorJson);
      } catch (parseError) {
        // If it's not valid JSON, still show the result but with a warning
        setResult(elementorJson);
        setError(`Warning: The generated output may not be valid JSON. Error: ${parseError.message}`);
      }
    } catch (err) {
      setError(err.message || 'Conversion failed');
      console.error('Conversion error:', err);
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Header />
      <main className="container mx-auto px-6 py-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* API Settings */}
          <ApiSettings
            apiKey={apiKey}
            setApiKey={setApiKey}
            modelName={modelName}
            setModelName={setModelName}
            apiUrl={apiUrl}
            setApiUrl={setApiUrl}
            apiProvider={apiProvider}
            setApiProvider={setApiProvider}
          />

          {/* Screenshot Uploader */}
          <ScreenshotUploader screenshots={screenshots} setScreenshots={setScreenshots} />

          {/* Code Input Section */}
          <CodeInput
            htmlCode={htmlCode}
            setHtmlCode={handleHtmlChange}
            cssCode={cssCode}
            setCssCode={setCssCode}
          />

          {/* Convert Button */}
          <div className="flex justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={convertToElementor}
              disabled={isConverting || !htmlCode.trim()}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
            >
              {isConverting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Converting...
                </>
              ) : (
                'Convert to Elementor JSON'
              )}
            </motion.button>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl"
            >
              {error}
            </motion.div>
          )}

          {/* Result Section */}
          {result && (
            <ConversionResult result={result} />
          )}
        </motion.div>
      </main>
    </div>
  );
}

export default App;